import type { Cents } from "./money";

/**
 * Cost Type — the simple Phase 2 classification of a proposed cost line.
 * Optional, because manual estimate lines like "Kitchen Renovation — $12,000"
 * legitimately represent mixed material + labor scope.
 */
export type CostType = "material" | "labor" | "other";

export const COST_TYPES: ReadonlyArray<{ id: CostType; displayName: string }> = [
  { id: "material", displayName: "Material" },
  { id: "labor", displayName: "Labor" },
  { id: "other", displayName: "Other" },
];

/**
 * Proposed Cost Line — one line of the project's renovation estimate.
 *
 * Phase 2 lines are manually entered all-in expected costs (tax included in
 * the single amount; the later estimating engine introduces quantities, unit
 * costs, and tax assumptions). `source` records provenance per the master
 * specification (Section 21): 'manual' now; assembly-generated lines arrive
 * in a later phase and will coexist in the same table.
 *
 * Identity rules (baseline readiness, spec Sections 11/27):
 *  - UUID ids, never reused;
 *  - lines that have ever been referenced (linked expenses, future
 *    snapshots) are archived, never physically deleted;
 *  - archived lines are excluded from current Proposed Rehab Cost but
 *    remain readable wherever referenced.
 *
 * Future dimensions (Room, Feature, Trade, Supplier) will be added as
 * nullable foreign keys by later migrations — they are deliberately NOT
 * faked as free text here. A line description like "Kitchen — $12,000" is
 * just a description.
 */
export interface ProposedCostLine {
  id: string;
  projectId: string;
  description: string;
  /** All-in expected cost, integer cents. */
  amountCents: Cents;
  categoryId: string | null;
  costType: CostType | null;
  /**
   * Optional Project Feature this line belongs to (at most one). Many
   * lines may share one feature — the future Assembly system generates
   * several lines beneath a feature. Unlinked lines remain fully valid.
   */
  featureId: string | null;
  /**
   * Provenance: 'manual' (user-entered) or 'assembly' (generated by an
   * assembly component — read-only in the line editor; assemblyComponentId
   * identifies the generator and keeps line identity stable across
   * regeneration).
   */
  source: "manual" | "assembly";
  assemblyComponentId: string | null;
  /** Spec §77: set when a profile rate wrote this line, so re-applying updates it in place. */
  calibrationRateId: string | null;
  sortOrder: number;
  notes: string;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProposedCostLineInput {
  description: string;
  amountCents: Cents;
  categoryId: string | null;
  costType: CostType | null;
  featureId?: string | null;
  notes: string;
}

export function validateProposedCostLineInput(input: ProposedCostLineInput): string[] {
  const problems: string[] = [];
  if (input.description.trim() === "") {
    problems.push("Description is required.");
  }
  if (!Number.isSafeInteger(input.amountCents)) {
    problems.push("Amount must be a valid dollar amount.");
  }
  return problems;
}
